import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { CurrentProvingContext } from 'context/CurrentProvingContext'
import { FormikActions } from 'formik'
import {
    CreateProvingForm,
    CreateProvingFormSchema,
} from 'forms/CreateProvingForm'
import {
    ParticipantDetailsForm,
    ParticipantDetailsFormSchema,
} from 'forms/ProvingDetailsForm/participants/ParticipantDetailsForm'
import { clearObjectIdsByPath, omitDeep, removeNullProperties } from 'helpers'
import { useCreateProvingCommand, useShowCommand } from 'hooks/useCommand'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import { Participant, Proving, TrackHotel, UserRole } from 'model'
import { PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'react-router5'
import {
    useProvingLazyQuery,
    useUpdateParticipantsMutation,
} from 'views/ProvingDetailsView/hooks'
import { ProvingInsertType } from '../../model'
import { MyBookingsList } from './MyBookingsList'
import {
    useCreateProvingMutation,
    useReadProvingsMyBookingsViewQuery,
} from './MyBookingsView.generated'

export type participantDetails = Partial<Participant> & {
  provingDateFrom: Date
  provingDateTo: Date
  provingStatus: number
  provingId: string
  provingTitle: string
  provingGroundTitle: string
  provingGroundCountryCode: string
}

const MyBookingsListView: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useRouter()

  const [updateParticipants] = useUpdateParticipantsMutation()
  const [panelState, showPanel, hidePanel] =
    useFormikPanelState<ProvingInsertType>()
  const [participantPanelState, showParticipantPanel, hideParticipantPanel] =
    useFormikPanelState()
  const [
    getProving,
    { loading: provingLoading, data: provingData, refetch: refetchProving },
  ] = useProvingLazyQuery()
  const showParticipant = React.useCallback(
    (participant: participantDetails) => {
      const curParticipant = participant as any
      getProving({
        variables: { id: curParticipant.provingId },
      })
      showParticipantPanel({
        ...curParticipant,
        accomodation: {
          ...curParticipant.accomodation,
          accomodation: curParticipant.accomodation.accomodation
            ? curParticipant.accomodation.accomodation._id
            : '',
        },
        trackHotels: {
          outward: curParticipant.trackHotels.outward.map(
            (out: TrackHotel) => ({
              ...out,
              hotel: out.hotel ? out.hotel._id : null,
            })
          ),
          return: curParticipant.trackHotels.return.map((ret: TrackHotel) => ({
            ...ret,
            hotel: ret.hotel ? ret.hotel._id : null,
          })),
          ...curParticipant.trackHotel,
        },
        ferry: {
          ...participant.ferry,
          outward: curParticipant.ferry.outward
            ? curParticipant.ferry.outward._id
            : null,
          return: curParticipant.ferry.return
            ? curParticipant.ferry.return._id
            : null,
        },
      })
    },
    [getProving, showParticipantPanel]
  )
  const showNewProving = React.useCallback(
    (proving: Partial<Proving>) => {
      router.navigate('provings.details', {
        id: proving.id,
        page: 'participants',
      })
    },
    [router]
  )

  const [selection, selectedItems] = useSelection<participantDetails>({
    getKey: (item) => item!._id!,
  })

  useCreateProvingCommand(10, () => showPanel({} as ProvingInsertType))
  const showProving = React.useCallback(
    (provingId: string) => {
      router.navigate('provings.details', { id: provingId })
    },
    [router]
  )
  useShowCommand(
    { name: 'proving', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        showProving(selectedItems[0]!.provingId)
      },
    },
    [selectedItems]
  )

  const [createProving] = useCreateProvingMutation()

  const { loading, data, refetch, error } = useReadProvingsMyBookingsViewQuery()

  const items =
    data && data.myBookings ? data.myBookings.filter((x) => !x._deleted) : []

  const byBookings: participantDetails[] = React.useMemo(() => {
    const temp: any[] = []
    items.forEach(
      (proving) =>
        proving.participants &&
        proving.participants.forEach((participant) => {
          const employeeIdReplaced =
            data &&
            data.principal &&
            data.principal.employeeId &&
            data.principal.employeeId.replace(/^0+/, 'P')

          if (
            participant.person &&
            (participant.person._id === data!.principal!._id ||
              participant.person._id === data!.principal!.employeeId ||
              employeeIdReplaced === participant.person._id)
          ) {
            temp.push({
              ...participant,
              provingDateFrom: participant.dateFrom,
              provingDateTo: participant.dateTo,
              provingTitle: proving.title,
              provingId: proving.id,
              provingStatus: proving.status,
              provingGroundTitle: proving.provingGround!.title || '',
              provingGroundCountryCode:
                proving.provingGround!.countryCode || '',
            })
          }
        })
    )
    return temp
  }, [data, items])

  const provingItem =
    !provingLoading && provingData && provingData.proving
      ? provingData.proving
      : undefined

  const handleSubmit = async (
    values: ProvingInsertType,
    formikActions: FormikActions<ProvingInsertType>
  ) => {
    formikActions.setSubmitting(true)

    const createdProvingJSON = await createProving({
      variables: { input: values },
    })
    const newProvingJson = createdProvingJSON as {
      data: { createProving: { id: string } }
    }
    const newProving: Partial<Proving> = {
      id: newProvingJson.data.createProving.id,
    }
    await refetch()

    formikActions.setSubmitting(false)
    hidePanel()
    showNewProving(newProving)
  }
  const handleParticipantSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)

      values = clearObjectIdsByPath(values, [
        'accomodation.accomodation',
        'ferry.outward',
        'ferry.return',
      ])

      const updateValues = removeNullProperties({
        orderId: values.orderId,
        dateTo: values.dateTo,
        dateFrom: values.dateFrom,
        note: values.note,
        percentage: values.percentage,
        flight: values.flight,
        accomodation: values.accomodation,
        trackHotelRequired: values.trackHotelRequired,
        trackHotels: values.trackHotels,
        rentalCar: values.rentalCar,
        group: values.group,
        temporaryPass: values.temporaryPass,
        passActivation: values.passActivation,
        permanentDriveAuthorization: values.permanentDriveAuthorization,
        singleDriveAuthorization: values.singleDriveAuthorization,
        dvEquipment: values.dvEquipment,
        partsTransport: values.partsTransport,
        driverTraining: values.driverTraining,
        ferry: values.ferry,
        nordschleife: values.nordschleife,
      })

      const { errors } = await updateParticipants({
        variables: {
          provingId: provingItem!._id,
          batch: [
            {
              _id: selectedItems[0]._id!,
              update: omitDeep(updateValues, ['__typename']),
            },
          ],
        },
      })

      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideParticipantPanel()
    },
    [
      hideParticipantPanel,
      provingItem,
      updateParticipants,
      selectedItems,
      refetch,
      selection,
    ]
  )
  return (
    <FlexSpinner loading={loading || provingLoading} error={error}>
      <MyBookingsList
        items={byBookings}
        onItemInvoked={showParticipant}
        selection={selection}
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new CreateProvingFormSchema()}
        headerText={t('model.proving.create')}
      >
        <CreateProvingForm />
      </FormikPanel>

      {provingItem && (
        <CurrentProvingContext.Provider value={[provingItem!, refetchProving]}>
          <FormikPanel
            {...participantPanelState}
            type={PanelType.large}
            validationSchema={
              new ParticipantDetailsFormSchema(
                provingItem.dateFrom,
                provingItem.dateTo
              )
            }
            onSubmit={handleParticipantSubmit}
          >
            <ParticipantDetailsForm />
          </FormikPanel>
        </CurrentProvingContext.Provider>
      )}
    </FlexSpinner>
  )
}

export const MyBookingsView: React.FC = () => (
  <>
    <ViewAuthorizer
      roles={[
        UserRole.Maintenancehead,
        UserRole.ProvingOrganisation,
        UserRole.Reader,
        UserRole.ResourceManager,
        UserRole.ServiceTeam,
        UserRole.SystemAdministrator,
        UserRole.ProvingCreator,
        UserRole.User,
      ]}
    >
      <StickyPane>
        <AppCommandBar />
        <MyBookingsListView />
      </StickyPane>
    </ViewAuthorizer>
  </>
)
